// Layout · campus v2
// ─────────────────────────────────────────────────────────────────────────
// FUNDACIÓN v2: el shell de navegación pasó de un HEADER horizontal a un
// SIDEBAR lateral (AppShell). Layout sigue siendo el envoltorio que usan TODAS
// las páginas del campus (24 páginas + AdminLayout lo envuelven), así que al
// cambiar AQUÍ el chrome, todas las páginas heredan el nuevo shell sin tocar
// rutas ni componentes de página.
//
// Contrato preservado:
//  · showHeader === false  → SIN shell (landing/login se renderizan a pelo).
//  · autenticado + showHeader → AppShell (sidebar) envuelve el contenido.
//  · no autenticado (pero showHeader) → contenido a pelo con footer (páginas
//    legales públicas: privacidad/términos/aviso-legal/contacto).
//  · El footer legal (disclaimer RGPD de riesgo) se conserva SIEMPRE que hay
//    shell o contenido público: es obligatorio.

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { isStubModeAllowed } from '../lib/env';
import AppShell from './AppShell';
import CookieBanner from './CookieBanner';
import { useInsideShell } from './CampusShell';
import { TELEGRAM_SUPPORT_URL } from '../lib/support-links';

interface LayoutProps {
  children: ReactNode;
  /** false = sin shell (landing/login). Default true. */
  showHeader?: boolean;
  /** Conservado por compatibilidad de API. El AppShell no usa botón "Atrás"
   *  (la navegación lateral lo hace innecesario). No-op en v2. */
  hideBackButton?: boolean;
}

/** Pie legal · disclaimer de riesgo (obligatorio) + enlaces. Se renderiza
 *  dentro del flujo del contenido para que aparezca tras la página. */
function LegalFooter(): JSX.Element {
  return (
    <footer className="mt-12 border-t border-ink/10 bg-canvas/60 px-6 py-10 text-sm text-ink/60 sm:px-10">
      <div className="mx-auto flex max-w-6xl flex-col gap-8 sm:flex-row sm:justify-between">
        <div>
          <p className="font-semibold text-ink/80">Robot Expert Advisor SL</p>
          <p className="mt-1 text-ink/55">Formación en robots de trading automatizado</p>
        </div>
        <nav aria-label="Pie de pagina" className="flex flex-wrap items-center gap-x-5 gap-y-2 sm:justify-end">
          <a
            href={TELEGRAM_SUPPORT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border border-brand-violet/40 bg-brand-violet/10 px-3 py-1.5 text-ink/85 transition hover:bg-brand-violet/20 hover:text-ink"
            aria-label="Soporte técnico en Telegram (se abre en una pestaña nueva)"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M21.94 4.6 18.6 20.36c-.25 1.1-.9 1.38-1.83.86l-5.05-3.72-2.44 2.35c-.27.27-.5.5-1.02.5l.36-5.16 9.4-8.5c.4-.36-.09-.56-.63-.2L5.16 13.3l-5-1.56c-1.08-.34-1.1-1.08.23-1.6L20.54 3.04c.9-.34 1.69.2 1.4 1.56Z" />
            </svg>
            Soporte (Telegram)
          </a>
          <Link to="/privacidad" className="hover:text-ink" aria-label="Politica de privacidad">Privacidad</Link>
          <Link to="/terminos" className="hover:text-ink" aria-label="Terminos y condiciones del servicio">Términos</Link>
          <Link to="/aviso-legal" className="hover:text-ink" aria-label="Aviso legal">Aviso legal</Link>
          <Link to="/contacto" className="hover:text-ink" aria-label="Pagina de contacto">Contacto</Link>
        </nav>
      </div>
      <div className="mt-8 w-full rounded-lg border border-red-300 bg-red-50 px-4 py-2.5 text-center text-[11px] leading-relaxed text-red-800 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-100/90 sm:text-xs">
        ⚠️ <strong>La inversión conlleva riesgo.</strong> Formación · no asesoramiento financiero personalizado · resultados pasados no garantizan futuros.
      </div>
      <p className="mt-6 text-center text-ink/30">
        &copy; {new Date().getFullYear()} Robotea · todos los derechos reservados.
        <span className="mx-1.5 text-ink/20">·</span>
        {/* Jaime 2026-06-17: en ~1 semana quitar "Beta Version" → dejar solo "V2". */}
        <span className="text-ink/25">V2 · Beta Version</span>
      </p>
    </footer>
  );
}

export default function Layout({ children, showHeader = true }: LayoutProps): JSX.Element {
  const { state } = useAuth();
  const authenticated = state.status === 'authenticated';
  // ¿Ya vivimos dentro del shell persistente (CampusShell)? Si es así, el rail,
  // la barra superior, los banners y el cookie banner ya están montados arriba
  // y PERSISTEN entre rutas. Aquí solo aportamos el contenido + el pie legal,
  // sin volver a montar el AppShell (eso causaba el remount → parpadeo).
  const insideShell = useInsideShell();

  const showStubBanner =
    isStubModeAllowed() && authenticated && state.identity.is_stub;

  // Banner permanente del modo REVISIÓN solo-preview. Se muestra siempre que la
  // sesión activa sea de revisión (mode='review'), para que quede clarísimo que
  // no es una sesión real. (El gate por host ya impidió crearla fuera de preview.)
  const showReviewBanner = authenticated && state.identity.mode === 'review';

  // showHeader === false → contenido a pelo (landing / login). Sin shell, sin
  // footer: esas páginas traen su propio marco completo.
  if (!showHeader) {
    return (
      <>
        {children}
        <CookieBanner />
      </>
    );
  }

  // Dentro del shell persistente (ruta envuelta por CampusShell) → modo
  // "solo contenido": el AppShell, los banners y el cookie banner ya viven
  // arriba (persisten entre rutas). Aquí únicamente inyectamos el contenido de
  // la página y el pie legal dentro del slot de contenido del shell.
  if (insideShell && authenticated) {
    return (
      <>
        {children}
        <LegalFooter />
      </>
    );
  }

  const stubBanner = showStubBanner ? (
    <div
      role="alert"
      className="border-b border-amber-400/40 bg-amber-500/15 px-4 py-2 text-center text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-200"
    >
      MODO STUB · auth fake (X-User-Email) · NO USAR EN PROD
    </div>
  ) : null;

  const reviewBanner = showReviewBanner ? (
    <div
      role="alert"
      data-testid="review-mode-banner"
      className="border-b border-brand-violet/40 bg-brand-violet/15 px-4 py-2 text-center text-xs font-semibold uppercase tracking-wider text-brand-indigo dark:text-brand-violet"
    >
      MODO PREVIEW · sesión de revisión · datos limitados
    </div>
  ) : null;

  // Autenticado → AppShell (sidebar). El contenido + footer legal viven dentro
  // del slot de contenido del shell.
  if (authenticated) {
    return (
      <>
        {stubBanner}
        {reviewBanner}
        {/* La búsqueda (modal GlobalSearch + atajo Cmd/Ctrl+K) la monta ahora el
            AppShell, controlada por su caja de búsqueda de la barra superior.
            Evitamos una segunda instancia aquí para no duplicar el listener. */}
        <AppShell>
          {children}
          <LegalFooter />
        </AppShell>
        <CookieBanner />
      </>
    );
  }

  // No autenticado pero con shell solicitado → páginas públicas legales.
  // Sin sidebar (no hay sesión), pero con el footer legal y un contenedor
  // de ancho consistente.
  return (
    <div className="flex min-h-full flex-col">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50
                   focus:rounded-lg focus:bg-brand-violet focus:px-4 focus:py-2
                   focus:text-sm focus:font-medium focus:text-white"
      >
        Saltar al contenido
      </a>
      <main id="main-content" tabIndex={-1} className="mx-auto w-full max-w-[1180px] flex-1 px-4 sm:px-8">
        {children}
      </main>
      <LegalFooter />
      <CookieBanner />
    </div>
  );
}
